import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ordersAPI, orderReviewsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Search, FileDown, Plus, CalendarRange, Loader2 } from 'lucide-react';
import { OrdersTable } from '../components/orders/OrdersTable';
import { DirectOrderModal } from '../components/orders/DirectOrderModal';
import { ORDER_CSV_HEADERS, orderToCsvRows, rowsToCsv, downloadCsv } from '../utils/orderCsvExport';
import { BulkActionBar } from '../components/bulk/BulkActionBar';

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(50);

  // Selection state for bulk CSV export — keyed by order.id, persists across pages.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Cache of every order object we've seen this session (current page + date-range
  // adds). Lets the export include orders from pages the user no longer has loaded
  // without needing per-id refetch.
  const [orderCache, setOrderCache] = useState(() => new Map());
  // Date-range quick-filter state
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeSelecting, setRangeSelecting] = useState(false);

  const allOnPageSelected = useMemo(
    () => orders.length > 0 && orders.every((o) => selectedIds.has(o.id)),
    [orders, selectedIds]
  );
  const selectedOnPageCount = useMemo(
    () => orders.filter((o) => selectedIds.has(o.id)).length,
    [orders, selectedIds]
  );
  const toggleOne = (id) => setSelectedIds((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const togglePage = () => setSelectedIds((cur) => {
    const next = new Set(cur);
    const everyOn = orders.length > 0 && orders.every((o) => next.has(o.id));
    if (everyOn) orders.forEach((o) => next.delete(o.id));
    else orders.forEach((o) => next.add(o.id));
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  // Select-by-date-range: fetch every order between rangeStart..rangeEnd and add
  // both their IDs (to selectedIds) and the full order objects (to orderCache).
  const selectByDateRange = async () => {
    if (!rangeStart && !rangeEnd) {
      toast.error('Pick at least a "From" or "To" date');
      return;
    }
    setRangeSelecting(true);
    try {
      const res = await ordersAPI.list({
        start_date: rangeStart || undefined,
        end_date: rangeEnd || undefined,
        limit: 1000,
      });
      const matched = res.data?.items || res.data?.data || [];
      if (matched.length === 0) {
        toast.info('No orders in that range');
        return;
      }
      setOrderCache((cur) => {
        const next = new Map(cur);
        matched.forEach((o) => next.set(o.id, o));
        return next;
      });
      setSelectedIds((cur) => {
        const next = new Set(cur);
        matched.forEach((o) => next.add(o.id));
        return next;
      });
      const rangeLabel = [rangeStart || '…', rangeEnd || '…'].join(' → ');
      toast.success(`Added ${matched.length} orders in ${rangeLabel} to selection`);
    } catch (e) {
      console.error('Range-select failed', e);
      toast.error('Failed to fetch orders in range');
    } finally {
      setRangeSelecting(false);
    }
  };

  // Direct Order Modal State
  const [directOrderOpen, setDirectOrderOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderForm, setOrderForm] = useState({
    customer_po_number: '',
    customer_po_date: '',
    currency: 'INR',
    payment_terms: '',
    delivery_terms: '',
    notes: '',
    ship_to_address: '',
    tax_percentage: 0,
  });
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [reviewStatuses, setReviewStatuses] = useState({});

  const fetchOrders = useCallback(async () => {
    try {
      const res = await ordersAPI.list({ search, page, limit });
      const items = res.data.items || res.data.data || [];
      setOrders(items);
      // Keep these orders in the cache so cross-page selection survives navigation.
      setOrderCache((cur) => {
        const next = new Map(cur);
        items.forEach((o) => next.set(o.id, o));
        return next;
      });
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to load orders', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [search, page, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    orderReviewsAPI.statuses().then((res) => setReviewStatuses(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [limit]);

  const selectedOrderRows = useMemo(
    () => Array.from(selectedIds).map((id) => orderCache.get(id)).filter(Boolean),
    [selectedIds, orderCache]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const handleCreateDirectOrder = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (orderItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setCreatingOrder(true);
    try {
      const subtotal = calculateOrderTotal();
      const taxPercentage = parseFloat(orderForm.tax_percentage) || 0;
      const taxAmount = subtotal * taxPercentage / 100;
      const grandTotal = subtotal + taxAmount;
      
      const data = {
        customer_id: selectedCustomer.id,
        customer_po_number: orderForm.customer_po_number,
        customer_po_date: orderForm.customer_po_date || null,
        currency: orderForm.currency,
        payment_terms: orderForm.payment_terms,
        delivery_terms: orderForm.delivery_terms,
        notes: orderForm.notes,
        ship_to_address: orderForm.ship_to_address || null,
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        grand_total: grandTotal,
        items: orderItems.map(item => ({
          part_id: item.part_id,
          part_internal_no: item.part_internal_no || '',
          description: item.description || '',
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          uom: item.uom,
          weight_grams: parseFloat(item.weight_grams) || 0,
          tooling_cost: parseFloat(item.tooling_cost) || 0,
          sample_cost: parseFloat(item.sample_cost) || 0,
          production_lead_time: item.production_lead_time || null,
          sample_lead_time: item.sample_lead_time || null,
          is_manual: item.is_manual || false,
        }))
      };
      
      const res = await ordersAPI.createDirect(data);
      toast.success(`Direct order ${res.data.order_number} created successfully`);
      setDirectOrderOpen(false);
      resetDirectOrderForm();
      fetchOrders();
      navigate(`/orders/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const resetDirectOrderForm = () => {
    setSelectedCustomer(null);
    setOrderItems([]);
    setOrderForm({
      customer_po_number: '',
      customer_po_date: '',
      currency: 'INR',
      payment_terms: '',
      delivery_terms: '',
      notes: '',
      ship_to_address: '',
      tax_percentage: 0,
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'badge-warning',
      confirmed: 'badge-success',
      in_production: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error'
    };
    return <Badge className={styles[status] || 'badge-default'}>{status}</Badge>;
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'INR' ? '\u20B9' : currency === 'USD' ? '$' : '\u20AC';
    return `${symbol}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const exportOrdersToCSV = async () => {
    try {
      let exportOrders;
      if (selectedIds.size > 0) {
        // Pull selected orders from the cache. Cache is populated on every
        // fetchOrders + by date-range selection, so all selected ids should
        // be present. If any are missing (e.g. orders that fell out of cache),
        // fetch them in one batched call.
        const inCache = [];
        const missing = [];
        selectedIds.forEach((id) => {
          const o = orderCache.get(id);
          if (o) inCache.push(o); else missing.push(id);
        });
        if (missing.length > 0) {
          // Last-resort: fetch full list (bounded) and pick the missing ones
          const res = await ordersAPI.list({ limit: 1000 });
          const all = res.data?.items || res.data?.data || [];
          const map = new Map(all.map((o) => [o.id, o]));
          missing.forEach((id) => { if (map.has(id)) inCache.push(map.get(id)); });
        }
        if (inCache.length === 0) {
          toast.error('Selected orders could not be loaded for export.');
          return;
        }
        exportOrders = inCache;
      } else {
        // No selection — fall back to "export all" behavior with confirmation
        if (!window.confirm(`No orders selected. Export ALL ${total} orders?`)) return;
        const res = await ordersAPI.list({ limit: 1000 });
        exportOrders = res.data?.data || res.data?.items || [];
      }
      const rows = exportOrders.flatMap(orderToCsvRows);
      const csvContent = rowsToCsv(ORDER_CSV_HEADERS, rows);
      downloadCsv(csvContent, `sales_orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${rows.length} line items from ${exportOrders.length} order(s)`);
    } catch (error) {
      console.error('Bulk SO CSV export failed', error);
      toast.error('Failed to export orders');
    }
  };

  const calculateOrderTotal = () => {
    return orderItems.reduce((sum, item) => {
      const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      const tooling = parseFloat(item.tooling_cost) || 0;
      const sample = parseFloat(item.sample_cost) || 0;
      return sum + lineTotal + tooling + sample;
    }, 0);
  };

  const calculateTaxAmount = () => {
    return calculateOrderTotal() * (parseFloat(orderForm.tax_percentage) || 0) / 100;
  };

  const calculateGrandTotal = () => {
    return calculateOrderTotal() + calculateTaxAmount();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">Sales Orders</h1>
          <p className="text-muted-foreground">Order Acceptance / Sales Confirmations</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={exportOrdersToCSV}
            data-testid="export-orders-csv-btn"
          >
            <FileDown className="mr-2 h-4 w-4" />
            {selectedIds.size > 0 ? `Export Selected (${selectedIds.size})` : 'Export to CSV'}
          </Button>
          {selectedIds.size > 0 && (
            <Button
              variant="ghost"
              onClick={clearSelection}
              data-testid="clear-orders-selection-btn"
            >
              Clear selection
            </Button>
          )}
          <Button 
            className="btn-primary"
            onClick={() => setDirectOrderOpen(true)}
            data-testid="create-direct-order-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Direct Order
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="card-dashboard">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order number, customer, PO number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" className="btn-primary">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Date-range quick-select for CSV export */}
      <Card className="card-dashboard">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1 flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground font-medium mb-1 block">From date</label>
                <Input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  data-testid="range-start-input"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground font-medium mb-1 block">To date</label>
                <Input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  data-testid="range-end-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={selectByDateRange}
                disabled={rangeSelecting || (!rangeStart && !rangeEnd)}
                data-testid="select-by-range-btn"
              >
                {rangeSelecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-1" />}
                Add range to selection
              </Button>
              {(rangeStart || rangeEnd) && (
                <Button
                  variant="ghost"
                  onClick={() => { setRangeStart(''); setRangeEnd(''); }}
                  data-testid="clear-range-btn"
                >
                  Clear dates
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Selection persists across pages — pick orders from multiple pages or by date range, then hit
            <span className="font-medium text-foreground"> &nbsp;Export Selected</span>.
            {selectedIds.size > 0 && (
              <span data-testid="selection-summary"> &nbsp;Currently <strong>{selectedIds.size}</strong> selected
                ({selectedOnPageCount} on this page).
              </span>
            )}
          </p>
        </CardContent>
      </Card>

      {/* Bulk actions on the current selection */}
      <BulkActionBar
        entity="orders"
        selectedIds={selectedIds}
        selectedRows={selectedOrderRows}
        onClear={clearSelection}
        onDone={fetchOrders}
        csvColumns={[]}
      />

      {/* Orders Table */}
      <OrdersTable
        orders={orders}
        loading={loading}
        total={total}
        page={page}
        totalPages={totalPages}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getStatusBadge={getStatusBadge}
        selectedIds={selectedIds}
        onToggleOne={toggleOne}
        onTogglePage={togglePage}
        allOnPageSelected={allOnPageSelected}
        reviewStatuses={reviewStatuses}
      />

      {/* Direct Order Modal */}
      <DirectOrderModal
        open={directOrderOpen}
        onOpenChange={setDirectOrderOpen}
        orderForm={orderForm}
        setOrderForm={setOrderForm}
        orderItems={orderItems}
        setOrderItems={setOrderItems}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        onCreateOrder={handleCreateDirectOrder}
        creatingOrder={creatingOrder}
        formatCurrency={formatCurrency}
        calculateOrderTotal={calculateOrderTotal}
        calculateTaxAmount={calculateTaxAmount}
        calculateGrandTotal={calculateGrandTotal}
        resetForm={resetDirectOrderForm}
      />
    </div>
  );
}
